/**
 * Axios instance for orval-generated API clients
 * Integrates with existing apiService pattern
 */
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { APIM_GATEWAY_URLS, CURRENT_ENVIRONMENT } from "../config/env";
import { triggerGlobalLogout } from "../utils/globalLogout";
import {
  triggerForbiddenToast,
  triggerServerErrorToast,
  triggerConnectionErrorToast,
  triggerRateLimitToast,
} from "../utils/globalToast";

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: APIM_GATEWAY_URLS[CURRENT_ENVIRONMENT],
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage (matches existing pattern)
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // Handle different error types (matches existing apiService pattern)
    switch (status) {
      case 401:
        triggerGlobalLogout();
        break;
      case 403:
        triggerForbiddenToast();
        break;
      case 429:
        triggerRateLimitToast();
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        triggerServerErrorToast();
        break;
      default:
        if (!error.response) {
          triggerConnectionErrorToast();
        }
    }

    return Promise.reject(error);
  },
);

/**
 * Custom instance for orval
 * Matches the signature expected by orval-generated code
 */
export const customInstance = <T>(
  config: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return axiosInstance.request<T>(config);
};

export default customInstance;
